import React, { useMemo } from "react";

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";

import { useRecentMappingActivities } from "../../api/getRecentMappingActivities";

import { DataMappingBatch, StatusBucket } from "../../types";

interface RecentDataMappingActivityProps {
  onViewAll?: () => void;
}

const getIconClass = (bucket: StatusBucket): string => {
  switch (bucket) {
    case "Succeeded":
      return "text-green-500";

    case "Failed":
      return "text-red-500";

    case "Partial":
      return "text-amber-500";
  }
};

const ActivityRow = ({
  item,
  isLast,
}: {
  item: DataMappingBatch;
  isLast: boolean;
}) => {
  const date = new Date(String(item.createdDate).replace(" ", "T"));

  const dateText = Number.isNaN(date.getTime())
    ? item.createdDate
    : date.toLocaleString();

  const Icon =
    item.bucket === "Succeeded" ? CheckCircleIcon : ExclamationTriangleIcon;

  const title =
    item.mappingType === "CDRC Status Reconciliation"
      ? `CDRC Status Reconciliation ${item.bucket}`
      : `${item.mappingType} Data Mapping ${item.bucket}`;

  return (
    <div
      className={`flex w-full items-center justify-between gap-2 bg-transparent p-2 text-left transition hover:bg-gray-50 ${
        isLast ? "" : "border-b border-gray-100"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Icon className={`h-3 w-3 shrink-0 ${getIconClass(item.bucket)}`} />

        <span className="min-w-0 text-[10px] leading-snug text-gray-700">
          {title}
        </span>
      </div>

      <span className="shrink-0 whitespace-nowrap text-[9px] text-gray-400">
        {dateText}
      </span>
    </div>
  );
};

const RecentDataMappingActivity: React.FC<RecentDataMappingActivityProps> = ({
  onViewAll,
}) => {
  const { data = [], isLoading, error } = useRecentMappingActivities(6);

  const recentActivities = useMemo(() => data.slice(0, 6), [data]);

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="flex h-44 items-center justify-center text-xs text-gray-400">
        Loading...
      </div>
    );
  } else if (error) {
    content = (
      <div className="flex h-44 items-center justify-center text-xs text-red-400">
        Failed to load activity.
      </div>
    );
  } else if (recentActivities.length === 0) {
    content = (
      <div className="flex h-44 items-center justify-center text-xs text-gray-400">
        No activity.
      </div>
    );
  } else {
    content = (
      <div className="flex flex-col">
        {recentActivities.map((item, index) => (
          <ActivityRow
            key={item.batchId}
            item={item}
            isLast={index === recentActivities.length - 1}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="h-full rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="rounded-t-xl bg-white px-4 py-2">
        <div className="flex items-center gap-3">
          <h2 className="m-0 text-left text-sm font-semibold text-primary">
            Recent Data Mapping Activity
          </h2>

          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="ml-auto border-0 bg-transparent p-0 text-xs text-gray-500 underline hover:opacity-80"
            >
              See more
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pb-4">{content}</div>
    </div>
  );
};

export default RecentDataMappingActivity;
